import { useState, useEffect, useCallback } from 'react';
import { thingsboardService } from '../services/thingsboard';

export interface TimeseriesData {
  [key: string]: {
    ts: number;
    value: string;
  }[];
}

export type TimeRange = 'realtime' | 'daily' | 'weekly' | 'monthly';

/**
 * Hook to fetch historical telemetry data from ThingsBoard
 */
export function useTelemetryHistory(
  targetId: string | null, 
  targetKeys: string[], 
  targetRange: TimeRange, 
  targetType: 'DEVICE' | 'ASSET' = 'DEVICE',
  aggMethod?: 'NONE' | 'AVG' | 'MIN' | 'MAX' | 'SUM'
) {
  const [data, setData] = useState<TimeseriesData>({});
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchHistoricalData = useCallback(async () => {
    if (!targetId /*|| targetRange === 'realtime'*/) {
      // if (targetRange === 'realtime') setData({}); // realtime is handled by useTelemetry
      return;
    }

    setLoading(true);
    try {
      let now = Date.now();
      let startTs = now;
      let interval = 0;
      let limit = 100;
      let agg: 'NONE' | 'AVG' | 'MIN' | 'MAX' | 'SUM' = 'NONE';

      // Auto-detect default aggregation if not explicitly provided
      const defaultAgg = targetKeys.includes('erv') 
        ? 'NONE'
        : targetKeys.some(k => k.includes('power') || k.includes('energy')) 
        ? 'MAX' 
        : 'AVG';

      agg = aggMethod || defaultAgg;

      switch (targetRange) {
        case 'realtime':
          startTs = now - 30 * 60 * 1000; // 30 minutes
          interval = 0;
          limit = 1000;
          break;
        case 'daily': {
          // Last 24 hours
          startTs = now - 24 * 60 * 60 * 1000;
          if (agg === 'NONE') {
            interval = 0;
            limit = 5000;
          } else {
            interval = 900000; // 15-minute intervals (96 points/day)
            limit = 100;
          }
          break;
        }
        case 'weekly': {
          // Last 7 days
          startTs = now - 7 * 24 * 60 * 60 * 1000;
          if (agg === 'NONE') {
            interval = 0;
            limit = 10000;
          } else {
            interval = 3600000; // 1-hour intervals (168 points/week)
            limit = 200;
          }
          break;
        }
        case 'monthly': {
          // Last 30 days
          startTs = now - 30 * 24 * 60 * 60 * 1000;
          if (agg === 'NONE') {
            interval = 0;
            limit = 10000;
          } else {
            interval = 21600000; // 6-hour intervals (120 points/month)
            limit = 200;
          }
          break;
        }
      }

      // For latching state telemetry like 'erv', extend fetchStartTs to 30 days ago to capture prior state
      let fetchStartTs = startTs;
      if (targetKeys.includes('erv')) {
        fetchStartTs = Math.min(startTs, now - 30 * 24 * 60 * 60 * 1000);
      }

      const result = await thingsboardService.getTimeseries(
        targetType,
        targetId,
        targetKeys.join(','),
        fetchStartTs,
        now,
        interval,
        limit,
        agg
      );
      setData(result);
      setError(null);
    } catch (err: any) {
      console.error('Failed to fetch historical telemetry', err);
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [targetId, targetType, targetKeys.join(','), targetRange, aggMethod]);

  useEffect(() => {
    fetchHistoricalData();
  }, [fetchHistoricalData]);

  return { data, loading, error, refetch: fetchHistoricalData };
}
